/**
 * CreateChore - the "post a chore" form (also used for editing)
 * -------------------------------------------------------------
 * One form serves both jobs. If the address has an :id in it, we load that
 * chore and save over it; otherwise we create a new one.
 *
 * Posting requires an active membership, so anyone without one is sent to the
 * subscribe page rather than being allowed to fill the form in for nothing.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CalendarDays, Lock, MapPin } from 'lucide-react';

import { choreApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import LocationFields from '../components/LocationFields';
import Loader from '../components/Loader';
import BackLink from '../components/BackLink';
import DayPicker, { dayKey } from '../components/DayPicker';

import { useI18n } from '../hooks/useI18n';
import {
  visitsFromDays,
  daysFromWeekly,
  horizonDate,
  describeVisit,
  longDate,
  longDateTime,
  HALF_HOURS,
  FLEXIBLE_TIME,
} from '../utils/schedule';

/**
 * The three shapes a job can have.
 *
 * "One day" is first and is the default, because it is still what most chores
 * are and the form should not make the common case feel like the awkward one.
 */
const SCHEDULE_MODES = [
  { id: 'none', label: 'createChore.modeOne', hint: 'createChore.modeOneHint' },
  { id: 'dates', label: 'createChore.modeDates', hint: 'createChore.modeDatesHint' },
  { id: 'weekly', label: 'createChore.modeWeekly', hint: 'createChore.modeWeeklyHint' },
];

const WEEKDAY_BUTTONS = [
  { day: 1, label: 'createChore.mon' },
  { day: 2, label: 'createChore.tue' },
  { day: 3, label: 'createChore.wed' },
  { day: 4, label: 'createChore.thu' },
  { day: 5, label: 'createChore.fri' },
  { day: 6, label: 'createChore.sat' },
  { day: 0, label: 'createChore.sun' },
];

/** Today + 3 days, formatted for a datetime-local input. */
function defaultDateTime() {
  const date = new Date();
  date.setDate(date.getDate() + 3);
  date.setHours(10, 0, 0, 0);
  return toInputValue(date);
}

/** Date -> "2025-08-14T10:00" (what a datetime-local input expects) */
function toInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export default function CreateChore() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canPostChores } = useAuth();
  const { t } = useI18n();

  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'Cleaning',
    pay: '',
    scheduledFor: defaultDateTime(),
    estimatedHours: 1,
  });
  const [location, setLocation] = useState({
    address: '',
    city: user?.location?.city || '',
    lat: user?.location?.lat ?? null,
    lng: user?.location?.lng ?? null,
  });

  /**
   * HOW OFTEN THIS JOB HAPPENS.
   *
   * Kept separate from `form` above because none of it is a plain text field -
   * the days come from a calendar, and the weekly pattern is generated rather
   * than typed. `form.scheduledFor` stays exactly what it always was and is
   * still the only thing the "One day" mode uses.
   */
  const [schedule, setSchedule] = useState({
    mode: 'none',
    days: [], // '2026-08-20' keys, for "Several days"
    weekdays: [], // 0-6, for "Every week"
    until: '', // 'YYYY-MM-DD', the closing date for "Every week"
    time: '10:00', // what time each visit starts
  });

  const [categories, setCategories] = useState([]);
  // Sent by the server so the calendar and the server agree about the limits -
  // see the meta route in backend/routes/chores.js. The fallbacks only matter
  // if that call fails, and they match what the server currently enforces.
  const [limits, setLimits] = useState({ maxRepeatMonths: 3, maxVisits: 100 });
  const [loading, setLoading] = useState(isEditing);
  /**
   * IS SOMEBODY ALREADY BOOKED ON THIS JOB?
   *
   * If they are, the only things still editable are the day and the time -
   * routes/chores.js refuses the rest, and the fields below are switched off
   * to match. Turning them off without saying why would read as the site
   * being broken, so there is a notice at the top of the form as well.
   *
   * The server is what enforces this. These are a courtesy, so nobody types
   * out a new description and only then finds out.
   */
  const [lockedToDates, setLockedToDates] = useState(false);

  /**
   * A booked chore from before the address was required, with none on it.
   * The one locked field that stays open - routes/chores.js explains why
   * filling it in is not changing what the student agreed to.
   */
  const [addressMissing, setAddressMissing] = useState(false);

  /**
   * "I AM FLEXIBLE" - the day is fixed, the hour is not.
   *
   * Plenty of jobs are a day rather than an hour: clear the garage on
   * Saturday, walk the dog some time on Tuesday. The form used to make
   * everybody name a time anyway, so posters picked one at random and a
   * student turned up at 10:00 for a job nobody minded about.
   *
   * The time boxes stay visible and switched off rather than disappearing,
   * because a control that vanishes leaves somebody wondering what they broke.
   * A nominal 09:00 is still sent - see models/Chore.js for why the stored
   * time matters even when it is not shown.
   */
  const [timeFlexible, setTimeFlexible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);

  useEffect(() => {
    choreApi
      .categories()
      .then((data) => {
        setCategories(data.categories);
        if (data.limits) setLimits(data.limits);
      })
      .catch(() => setCategories([]));
  }, []);

  // When editing, load the existing chore into the form.
  useEffect(() => {
    if (!isEditing) return;

    choreApi
      .get(id)
      .then(({ chore }) => {
        setLockedToDates(Boolean(chore.assignedStudent || chore.backupStudent));
        setAddressMissing(!chore.location?.address);
        setTimeFlexible(Boolean(chore.timeFlexible));
        setForm({
          title: chore.title,
          description: chore.description,
          category: chore.category,
          pay: String(chore.pay),
          scheduledFor: toInputValue(new Date(chore.scheduledFor)),
          estimatedHours: chore.estimatedHours,
        });

        /**
         * EDITING A MULTI-DAY JOB SHOWS THE REMAINING DAYS INDIVIDUALLY, even
         * when it was originally set up as "every Wednesday and Saturday".
         *
         * That is deliberate, and the alternative is worse. Loading it back as
         * a weekly pattern would mean regenerating the dates from the pattern
         * on save - and regeneration starts from tomorrow, so every visit
         * already worked would silently vanish from the booking. A fortnight
         * into a three-month arrangement, saving a typo in the description
         * would quietly delete the first month of it.
         *
         * Past visits are left out of the picker for the same reason they
         * cannot be edited: they have already happened. Only what is still to
         * come is up for changing.
         */
        const upcoming = (chore.visits || [])
          .filter((v) => v.status !== 'cancelled' && new Date(v.start) > new Date());

        if (upcoming.length > 0) {
          const first = new Date(upcoming[0].start);
          setSchedule((s) => ({
            ...s,
            mode: upcoming.length > 1 ? 'dates' : 'none',
            days: upcoming.map((v) => dayKey(new Date(v.start))),
            time: `${String(first.getHours()).padStart(2, '0')}:${String(first.getMinutes()).padStart(2, '0')}`,
          }));
        }
        setLocation({
          address: chore.location.address || '',
          city: chore.location.city || '',
          lat: chore.location.lat ?? null,
          lng: chore.location.lng ?? null,
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const errorFor = (field) => fieldErrors.find((e) => e.field === field)?.message;

  const horizon = useMemo(() => horizonDate(limits.maxRepeatMonths), [limits.maxRepeatMonths]);

  /**
   * "One day" mode, split into the two halves the screen now asks for.
   *
   * `form.scheduledFor` is still exactly the string it always was -
   * 'YYYY-MM-DDTHH:MM', the shape a datetime-local produced - so the payload,
   * the edit-loading and the validation all carry on unchanged. Only the way
   * it is TYPED has moved off the browser's own control.
   */
  const oneDayKey = form.scheduledFor ? form.scheduledFor.slice(0, 10) : '';
  const oneDayTime = form.scheduledFor ? form.scheduledFor.slice(11, 16) : '10:00';
  const setOneDay = (key, time) =>
    setForm((f) => ({ ...f, scheduledFor: `${key || oneDayKey}T${time || oneDayTime}` }));

  /**
   * The days currently chosen, whichever way they were chosen.
   *
   * "Every week" generates its days from the pattern rather than storing them,
   * so that changing a weekday or the closing date updates the list at once.
   * The poster sees the actual dates either way - a pattern nobody can see the
   * consequences of is how you agree to twenty-six visits by accident.
   */
  const chosenDays = useMemo(() => {
    if (schedule.mode === 'dates') return [...schedule.days].sort();
    if (schedule.mode === 'weekly') {
      return daysFromWeekly(schedule.weekdays, schedule.until, limits.maxVisits);
    }
    return [];
  }, [schedule, limits.maxVisits]);

  /**
   * The time actually sent to the server.
   *
   * A flexible chore still stores a real moment - everything from the
   * 24-hour rule to the clash check against a student's other work reads one.
   * FLEXIBLE_TIME is what goes in, and models/Chore.js explains why it is
   * nine in the morning rather than midnight.
   */
  const effectiveTime = timeFlexible ? FLEXIBLE_TIME : schedule.time;

  const visits = useMemo(
    () =>
      schedule.mode === 'none'
        ? []
        : visitsFromDays(chosenDays, effectiveTime, form.estimatedHours),
    [schedule.mode, chosenDays, effectiveTime, form.estimatedHours]
  );

  const perVisit = Number(form.pay) || 0;
  const totalPay = Math.round(perVisit * Math.max(visits.length, 1) * 100) / 100;
  const overLimit = visits.length > limits.maxVisits;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setFieldErrors([]);
    setBusy(true);

    // "One day" sends exactly what it always sent. Anything else sends the
    // whole list of times, plus how they were chosen so the chore can describe
    // itself later. `scheduledFor` is still sent either way, pointing at the
    // first visit - the marketplace and the job alerts read it.
    const multi = schedule.mode !== 'none' && visits.length > 0;

    const payload = {
      ...form,
      timeFlexible,
      pay: Number(form.pay),
      estimatedHours: Number(form.estimatedHours),
      scheduledFor: multi
        ? visits[0].start
        : new Date(timeFlexible ? `${oneDayKey}T${FLEXIBLE_TIME}` : form.scheduledFor).toISOString(),
      location,
      ...(multi
        ? {
          visits,
          repeat: {
            mode: schedule.mode,
            weekdays: schedule.mode === 'weekly' ? schedule.weekdays : [],
            until: schedule.mode === 'weekly' && schedule.until ? schedule.until : null,
          },
        }
        : {}),
    };

    if (schedule.mode !== 'none' && visits.length === 0) {
      setError(
        schedule.mode === 'weekly'
          ? t('createChore.pickWeekly')
          : t('createChore.pickDays')
      );
      setBusy(false);
      return;
    }

    try {
      if (isEditing) {
        await choreApi.update(id, payload);
        navigate(`/dashboard/chores/${id}`);
      } else {
        const { chore } = await choreApi.create(payload);
        navigate(`/dashboard/chores/${chore._id}`);
      }
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.fieldErrors || []);
      if (err.code === 'SUBSCRIPTION_REQUIRED') navigate('/subscribe');
    } finally {
      setBusy(false);
    }
  };

  // ---- No membership: explain rather than let them fill in a dead form ----
  // While the site is free canPostChores is always true for posters, so this
  // block simply never appears.
  if (!isEditing && !canPostChores) {
    return (
      <div className="container section">
        <div className="card" style={{ maxWidth: 620, margin: '0 auto' }}>
          <div className="alert alert-warning">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>{t('createChore.needMembership')}</strong>
              <br />
              <span className="text-sm">
                {t('createChore.fiveAYear')}
              </span>
            </div>
          </div>
          <Link to="/subscribe" className="btn btn-primary btn-block" style={{ marginTop: 'var(--space-sm)' }}>
            {t('createChore.seeMembership')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) return <Loader label={t('common.loadingChore')} />;

  return (
    <div className="container section" style={{ maxWidth: 760 }}>
      <BackLink to="/dashboard" label={t('common.backToMyChores')} />

      <div className="page-header">
        <h1>{isEditing ? t('createChore.titleEdit') : t('createChore.titleNew')}</h1>
        <p>
          {t('createChore.subtitle')}
        </p>
      </div>

      <div className="card">
        <form className="form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <div>
                {error}
                {fieldErrors.length > 0 && (
                  <ul>
                    {fieldErrors.map((e) => (
                      <li key={e.field}>{e.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Why half this form is switched off. A form full of dead boxes
              with no explanation is the clearest way to make a working site
              look broken - and this one is deliberate, so it says so. */}
          {lockedToDates && (
            <div className="alert alert-info">
              <Lock size={18} aria-hidden="true" />
              <div>
                <strong>{t('createChore.lockedTitle')}</strong>
                <br />
                <span className="text-sm">{t('createChore.lockedBody')}</span>
              </div>
            </div>
          )}

          {/* The one exception to the lock, said out loud - otherwise a poster
              reads "only the day and time can change" and never scrolls down
              to the one other box that is open. */}
          {lockedToDates && addressMissing && (
            <div className="alert alert-warning">
              <MapPin size={18} aria-hidden="true" />
              <div className="text-sm">{t('createChore.addMissingAddress')}</div>
            </div>
          )}

          <div className="field">
            <label htmlFor="title">{t('createChore.fieldTitle')} *</label>
            <input
              id="title"
              name="title"
              type="text"
              value={form.title}
              onChange={handleChange}
              maxLength={120}
              placeholder={t('createChore.titlePlaceholder')}
              aria-invalid={Boolean(errorFor('title'))}
              disabled={lockedToDates}
              required
            />
            {errorFor('title') && <span className="field-error">{errorFor('title')}</span>}
          </div>

          <div className="field">
            <label htmlFor="description">{t('createChore.whatNeedsDoing')} *</label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleChange}
              maxLength={2000}
              rows={6}
              placeholder={t('createChore.descriptionPlaceholder')}
              aria-invalid={Boolean(errorFor('description'))}
              disabled={lockedToDates}
              required
            />
            <span className="hint">{form.description.length}/2000 - at least 20 characters</span>
            {errorFor('description') && <span className="field-error">{errorFor('description')}</span>}
          </div>

          <div className="form-row">
            <div className="field">
              <label htmlFor="category">{t('createChore.category')} *</label>
              <select
                id="category"
                name="category"
                value={form.category}
                onChange={handleChange}
                disabled={lockedToDates}
                required
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              {/* On a multi-day job this is what ONE visit pays, not the whole
                  booking - so the label has to say so where somebody is
                  typing, not further down the page. Getting this wrong by a
                  factor of six is the obvious way to be badly out of pocket. */}
              <label htmlFor="pay">
                {schedule.mode === 'none' ? `${t('createChore.payment')} *` : `${t('createChore.paymentPerVisit')} *`}
              </label>
              <input
                id="pay"
                name="pay"
                type="number"
                min="1"
                max="1000"
                step="0.5"
                value={form.pay}
                onChange={handleChange}
                placeholder="30"
                aria-invalid={Boolean(errorFor('pay'))}
                disabled={lockedToDates}
                required
              />
              {schedule.mode !== 'none' && visits.length > 0 && perVisit > 0 && (
                <span className="hint">
                  €{perVisit} × {visits.length} {visits.length === 1 ? 'visit' : 'visits'} = €{totalPay}
                </span>
              )}
              {errorFor('pay') && <span className="field-error">{errorFor('pay')}</span>}
            </div>
          </div>

          <hr className="divider" />

          <h3 style={{ fontSize: 'var(--text-h4)' }}>{t('createChore.whenTitle')}</h3>

          {/* The three shapes a job can have. Radios rather than a dropdown
              because all three have to be visible: somebody who does not know
              the site can post several days will never go looking in a menu
              for it. */}
          <fieldset className="schedule-modes">
            <legend className="sr-only">{t('createChore.whenLegend')}</legend>
            {SCHEDULE_MODES.map((option) => (
              <label
                key={option.id}
                className={`schedule-mode${schedule.mode === option.id ? ' is-chosen' : ''}`}
              >
                <input
                  type="radio"
                  name="scheduleMode"
                  value={option.id}
                  checked={schedule.mode === option.id}
                  onChange={() => setSchedule((s) => ({ ...s, mode: option.id }))}
                />
                <span className="schedule-mode-label">{t(option.label)}</span>
                <span className="schedule-mode-hint">{t(option.hint)}</span>
              </label>
            ))}
          </fieldset>

          {/* The one change that is still allowed carries a cost, so say what
              it is BEFORE they make it rather than in a notification
              afterwards. A poster who knows the student can walk away is a
              poster who asks them first. */}
          {lockedToDates && (
            <div className="alert alert-warning">
              <AlertTriangle size={18} aria-hidden="true" />
              <div className="text-sm">{t('createChore.lockedDatesWarning')}</div>
            </div>
          )}

          {/* ---- One day ----

              The date is picked on OUR calendar rather than in the browser's
              own <input type="date">. That control is drawn by the browser in
              whatever format the machine is set to, and on an English setting
              that is 08/19/2026 - month first, on a site for a school in
              Italy. Setting lang="en-GB" on the document does not fix it:
              Chrome uses its own locale for these controls and ignores the
              page. This is the same conclusion MyOffer.jsx reached about
              <input type="time"> ("5:00 PM"), and the same answer: draw it
              ourselves, so everybody sees the same thing. */}
          {/* Above the time, not below it: this decides whether the box
              underneath means anything, and somebody reads down the page. */}
          <div className="field">
            <label
              htmlFor="timeFlexible"
              className="row"
              style={{ gap: 'var(--space-xs)', alignItems: 'flex-start', cursor: 'pointer' }}
            >
              <input
                id="timeFlexible"
                type="checkbox"
                checked={timeFlexible}
                onChange={(event) => setTimeFlexible(event.target.checked)}
                className="terms-tickbox"
              />
              <span style={{ fontWeight: 'var(--weight-regular)', color: 'var(--color-text)' }}>
                {t('createChore.flexibleLabel')}
              </span>
            </label>
            <span className="hint">{t('createChore.flexibleHint')}</span>
          </div>

          {schedule.mode === 'none' && (
            <>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="oneDayTime">{t('createChore.whatTime')} *</label>
                  <select
                    id="oneDayTime"
                    value={oneDayTime}
                    onChange={(e) => setOneDay(oneDayKey, e.target.value)}
                    disabled={timeFlexible}
                    required
                  >
                    {HALF_HOURS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="estimatedHours">{t('createChore.howLong')}</label>
                  <input
                    id="estimatedHours"
                    name="estimatedHours"
                    type="number"
                    min="0.5"
                    max="24"
                    step="0.5"
                    value={form.estimatedHours}
                    onChange={handleChange}
                    disabled={lockedToDates}
                  />
                </div>
              </div>

              <div className="field">
                <label>{t('createChore.whichDay')} *</label>
                <DayPicker
                  selected={oneDayKey ? [oneDayKey] : []}
                  maxDate={horizon}
                  onToggle={(key) => setOneDay(key, oneDayTime)}
                />
                {form.scheduledFor && (
                  <span className="hint">{longDateTime(form.scheduledFor)}</span>
                )}
                {errorFor('scheduledFor') && (
                  <span className="field-error">{errorFor('scheduledFor')}</span>
                )}
              </div>
            </>
          )}

          {/* ---- Several days, or every week ---- */}
          {schedule.mode !== 'none' && (
            <>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="visitTime">{t('createChore.whatTimeEach')} *</label>
                  {/* A dropdown, not <input type="time">, for the same reason
                      the date is our own calendar: the browser draws that one
                      in the machine's locale, which on an English setting is
                      "5:00 PM". 24-hour clock, same for everybody. */}
                  <select
                    id="visitTime"
                    value={schedule.time}
                    onChange={(e) => setSchedule((s) => ({ ...s, time: e.target.value }))}
                    disabled={timeFlexible}
                    required
                  >
                    {HALF_HOURS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="estimatedHours">{t('createChore.howLongEach')}</label>
                  <input
                    id="estimatedHours"
                    name="estimatedHours"
                    type="number"
                    min="0.5"
                    max="24"
                    step="0.5"
                    value={form.estimatedHours}
                    onChange={handleChange}
                    disabled={lockedToDates}
                  />
                </div>
              </div>

              {schedule.mode === 'dates' && (
                <div className="field">
                  <label>{t('createChore.whichDays')} *</label>
                  <span className="hint">
                    {t('createChore.whichDaysHint')}
                  </span>
                  <DayPicker
                    selected={schedule.days}
                    maxDate={horizon}
                    onToggle={(key) =>
                      setSchedule((s) => ({
                        ...s,
                        days: s.days.includes(key)
                          ? s.days.filter((d) => d !== key)
                          : [...s.days, key],
                      }))
                    }
                  />
                </div>
              )}

              {schedule.mode === 'weekly' && (
                <>
                  <div className="field">
                    <label>{t('createChore.whichWeekdays')} *</label>
                    <div className="weekday-picker">
                      {WEEKDAY_BUTTONS.map((wd) => {
                        const on = schedule.weekdays.includes(wd.day);
                        return (
                          <button
                            key={wd.day}
                            type="button"
                            className={`weekday-btn${on ? ' is-chosen' : ''}`}
                            aria-pressed={on}
                            onClick={() =>
                              setSchedule((s) => ({
                                ...s,
                                weekdays: on
                                  ? s.weekdays.filter((d) => d !== wd.day)
                                  : [...s.weekdays, wd.day],
                              }))
                            }
                          >
                            {t(wd.label)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="field">
                    <label>{t('createChore.until')} *</label>
                    {/* Our calendar, not <input type="date">. That control put
                        the month first on any browser set to US English, which
                        is how this was reported: a closing date of 14 November
                        offered as 11/14/2026. The page saying lang="en-GB"
                        does not move it - Chrome uses its own locale for these
                        and ignores the document. Tapping a day on a calendar
                        has no format to get wrong at all. */}
                    <span className="hint">
                      Tap the last day it should run. Up to {limits.maxRepeatMonths} months ahead -
                      you can always cancel or change it later.
                    </span>
                    <DayPicker
                      selected={schedule.until ? [schedule.until] : []}
                      maxDate={horizon}
                      onToggle={(key) =>
                        setSchedule((s) => ({ ...s, until: s.until === key ? '' : key }))
                      }
                    />
                    {schedule.until && (
                      <span className="hint">
                        {t('createChore.runsUntil')} <strong>{longDate(schedule.until)}</strong>
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* ---- What they have actually chosen, spelled out ----
                  A pattern whose consequences are invisible is how somebody
                  agrees to twenty-six visits without meaning to. The count,
                  the money and the first and last dates are all here before
                  the Post button, not after it. */}
              {visits.length > 0 && (
                <div className={`schedule-summary${overLimit ? ' is-over' : ''}`}>
                  <div className="schedule-summary-head">
                    <CalendarDays size={18} aria-hidden="true" />
                    <strong>
                      {visits.length} {visits.length === 1 ? 'visit' : 'visits'}
                      {perVisit > 0 && (
                        <> · €{perVisit} each · <span className="schedule-total">€{totalPay} in all</span></>
                      )}
                    </strong>
                  </div>

                  {overLimit ? (
                    <p className="schedule-summary-line">
                      That is more than {limits.maxVisits} visits, which is the most one booking can
                      hold. Choose a nearer finishing date, or fewer days a week.
                    </p>
                  ) : (
                    <p className="schedule-summary-line">
                      First: {describeVisit(visits[0], timeFlexible)}
                      {visits.length > 1 && <> · Last: {describeVisit(visits[visits.length - 1], timeFlexible)}</>}
                    </p>
                  )}

                  {/* Every date, for anyone who wants to check them rather
                      than trust the count. Closed by default so it does not
                      bury the rest of the form. */}
                  {!overLimit && visits.length > 1 && (
                    <details className="schedule-dates">
                      <summary>{t('createChore.seeEveryDate')}</summary>
                      <ul>
                        {visits.map((v) => (
                          <li key={v.start}>{describeVisit(v, timeFlexible)}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </>
          )}

          <hr className="divider" />

          <h3 style={{ fontSize: 'var(--text-h4)' }}>{t('createChore.whereIsIt')}</h3>
          <LocationFields
            value={location}
            onChange={setLocation}
            showAddress
            disabled={lockedToDates}
            addressDisabled={lockedToDates && !addressMissing}
          />

          <button type="submit" className="btn btn-primary btn-block" disabled={busy || overLimit}>
            {busy ? t('common.saving') : isEditing ? t('createChore.saveChanges') : t('createChore.postIt')}
          </button>
        </form>
      </div>
    </div>
  );
}
